import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage, SUPPORTED_LANGUAGES } from '../context/LanguageContext';
import { useMobileDetection } from '../hooks/useIsMobile';
import { getVersionDisplay, getEnvironment } from '../lib/version';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import GlobalSearch from './search/GlobalSearch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Users, 
  Building2, 
  Factory,
  Calendar, 
  ClipboardList,
  BarChart3, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  UserCog,
  Database,
  Upload,
  PanelLeftClose,
  PanelLeftOpen,
  Shield,
  Sun,
  Moon,
  User,
  FileText,
  Rocket,
  Globe,
  Clock,
  MessageSquare,
  Bell,
  HelpCircle,
  ChevronDown,
  Smartphone,
  ArrowRight,
  Ticket,
  Mountain,
  FileCheck
} from 'lucide-react';
import { getAssetIcon } from '../utils/industryIcons';

const Layout = () => {
  const { api, user, logout, isImpersonating } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMobileDetection();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('equipr_sidebar_collapsed');
    return saved ? JSON.parse(saved) : false; // Default expanded
  });
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [mobileBannerDismissed, setMobileBannerDismissed] = useState(() => {
    return sessionStorage.getItem('mobile_banner_dismissed') === 'true';
  });

  // Save sidebar preference
  useEffect(() => {
    localStorage.setItem('equipr_sidebar_collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleDismissMobileBanner = () => {
    setMobileBannerDismissed(true);
    sessionStorage.setItem('mobile_banner_dismissed', 'true');
  };

  const handleSwitchToMobile = () => {
    // Use full page navigation since tenant-mobile is a separate router context
    window.location.href = '/tenant-mobile';
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore errors
    }
    logout();
    navigate('/login');
  };

  const isOperator = user?.role === 'operator';
  const isAdmin = user?.role === 'admin';
  const isLightMode = theme === 'light';

  // State for Getting Started progress
  const [gettingStartedProgress, setGettingStartedProgress] = useState(null);
  
  // Fetch Getting Started progress (only for authenticated admins)
  useEffect(() => {
    const fetchProgress = async () => {
      // Ensure user is fully authenticated with client_id
      if (!api || !user || !user.client_id || isOperator) return;
      
      try {
        const [checklistRes, onboardingRes] = await Promise.all([
          api.get('/onboarding/checklist-status'),
          api.get('/users/me/onboarding')
        ]);
        const wizardComplete = onboardingRes.data?.wizard_completed || false;
        const items = checklistRes.data?.items || [];
        const completedItems = items.filter(i => i.completed && !i.optional).length;
        const totalRequired = items.filter(i => !i.optional).length;
        const total = totalRequired + 1; // +1 for onboarding wizard
        const completed = completedItems + (wizardComplete ? 1 : 0);
        setGettingStartedProgress({ 
          completed, 
          total, 
          allDone: checklistRes.data?.all_required_complete && wizardComplete, 
          dismissed: checklistRes.data?.checklist_dismissed 
        });
      } catch (e) {
        // Silently fail - user may have dismissed or is on basic tier
        console.log('Getting started fetch skipped:', e.message);
      }
    };
    
    // Small delay to ensure auth is fully established
    const timer = setTimeout(fetchProgress, 500);
    return () => clearTimeout(timer);
  }, [api, user?.client_id, isOperator]);

  // Main navigation items for sidebar
  // Get industry-specific asset icon
  const AssetIcon = getAssetIcon(user?.industry);
  
  const navItems = isOperator ? [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/my-jobs', icon: ClipboardList, label: t('nav.myJobs') },
  ] : [
    // Add Getting Started at the top if not complete and not dismissed
    ...(gettingStartedProgress && !gettingStartedProgress.allDone && !gettingStartedProgress.dismissed ? [
      { to: '/getting-started', icon: Rocket, label: 'Getting Started', badge: `${gettingStartedProgress.completed}/${gettingStartedProgress.total}`, highlight: true },
    ] : []),
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/schedule', icon: Calendar, label: t('nav.schedule') },
    { to: '/customers', icon: Building2, label: 'Customers & Sites' },
    { to: '/suppliers', icon: Factory, label: 'Subcontractors' },
    { to: '/equipment', icon: AssetIcon, label: t('nav.equipment') },
    { to: '/operators', icon: Users, label: t('nav.operators') },
    { to: '/tips-materials', icon: Mountain, label: 'Tips & Materials' },
    { to: '/quotes', icon: FileCheck, label: 'Quotes' },
    { to: '/dockets', icon: FileText, label: t('nav.dockets') },
    { to: '/compliance', icon: Shield, label: t('nav.compliance') },
    { to: '/timesheets', icon: Clock, label: 'Timesheets' },
    { to: '/chat', icon: MessageSquare, label: 'Messages' },
    { to: '/forms', icon: ClipboardList, label: t('nav.forms') },
    { to: '/reports', icon: BarChart3, label: t('nav.reports') },
    ...(isAdmin ? [
      { to: '/data-management', icon: Database, label: t('nav.dataManagement') },
      { to: '/data-import', icon: Upload, label: 'Data Import' },
    ] : []),
  ];

  // Sidebar styling
  // Navigation stays dark in both light and dark mode for professional look
  const sidebarBg = 'bg-zinc-900';
  const sidebarText = 'text-zinc-400';
  const sidebarActiveText = 'text-white';
  const sidebarHover = 'hover:bg-zinc-800';
  const sidebarActiveBg = 'bg-zinc-800';
  const sidebarBorder = 'border-zinc-800';

  // Top bar styling - also stays dark
  const topBarBg = 'bg-zinc-900 border-b border-zinc-800';
  const topBarText = 'text-zinc-400';

  const NavItem = ({ to, icon: Icon, label, badge, highlight }) => {
    const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
    
    // Highlight colors - always use dark mode colors since nav is always dark
    const highlightBg = 'bg-[#ffd108]/20';
    const highlightText = 'text-[#ffd108]';
    const highlightHover = 'hover:bg-[#ffd108]/30';
    const highlightBorder = 'border border-[#ffd108]/30';
    
    if (sidebarCollapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <NavLink
              to={to}
              onClick={() => setMobileMenuOpen(false)}
              className={`relative flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${
                highlight 
                  ? `${highlightBg} ${highlightText} ${highlightHover}`
                  : isActive
                    ? `${sidebarActiveBg} ${sidebarActiveText}`
                    : `${sidebarText} ${sidebarHover}`
              }`}
            >
              <Icon className="h-5 w-5" />
              {badge && (
                <span className="absolute -top-1 -right-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#ffd108] text-black">
                  {badge}
                </span>
              )}
            </NavLink>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10} className="font-medium">
            {label} {badge && `(${badge})`}
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <NavLink
        to={to}
        onClick={() => setMobileMenuOpen(false)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
          highlight
            ? `${highlightBg} ${highlightText} ${highlightHover} ${highlightBorder}`
            : isActive
              ? `${sidebarActiveBg} ${sidebarActiveText}`
              : `${sidebarText} ${sidebarHover}`
        }`}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className="truncate flex-1">{label}</span>
        {badge && (
          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#ffd108] text-black">
            {badge}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background flex flex-col">
        {/* Mobile Banner - Shows when on mobile device */}
        {isMobile && !mobileBannerDismissed && (
          <div className="fixed top-0 left-0 right-0 bg-[#ffd108] text-[#1a1a1a] px-4 py-3 z-[60] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              <span className="text-sm font-medium">Switch to mobile view for a better experience</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] h-8 px-3"
                onClick={handleSwitchToMobile}
                data-testid="switch-to-mobile-btn"
              >
                Open Mobile
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <button
                onClick={handleDismissMobileBanner}
                className="p-1 hover:bg-[#1a1a1a]/10 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
        
        {/* Top Navigation Bar */}
        <header className={`fixed ${isMobile && !mobileBannerDismissed ? 'top-12' : 'top-0'} left-0 right-0 h-12 ${topBarBg} flex items-center justify-between px-4 z-50 transition-all`}>
          {/* Left side - Logo & Mobile menu */}
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden p-2 rounded-md ${topBarText} hover:bg-gray-100 dark:hover:bg-zinc-800`}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded overflow-hidden">
                <img src="/equipr-logo.png" alt="EQUIPR" className="w-full h-full object-cover" />
              </div>
              <span className="font-heading font-bold text-sm hidden sm:block text-white">
                {user?.company_name || 'EQUIPR CORE'}
              </span>
              {/* Demo Badge */}
              {user?.is_demo && (
                <Badge 
                  variant="outline" 
                  className="ml-2 bg-amber-500/20 text-amber-600 border-amber-500/50 font-semibold text-xs"
                  data-testid="demo-badge"
                >
                  DEMO
                </Badge>
              )}
            </div>
          </div>

          {/* Center - Search (desktop) */}
          <div className="hidden md:flex flex-1 max-w-md mx-8">
            <GlobalSearch />
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-1">
            {/* Product Roadmap */}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/product-roadmap')}
                  className={`hidden sm:flex gap-1.5 ${topBarText}`}
                >
                  <Rocket className="h-4 w-4" />
                  <span className="hidden lg:inline text-xs">Roadmap</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>Product Roadmap</TooltipContent>
            </Tooltip>

            {/* Help */}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={`h-8 w-8 ${topBarText}`}
                  onClick={() => navigate('/help')}
                  data-testid="help-nav-btn"
                >
                  <HelpCircle className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Help Centre</TooltipContent>
            </Tooltip>

            {/* Support */}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className={`h-8 w-8 ${topBarText}`}
                  onClick={() => navigate('/support')}
                >
                  <Ticket className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Support</TooltipContent>
            </Tooltip>

            {/* Notifications */}
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className={`h-8 w-8 ${topBarText} relative`}>
                  <Bell className="h-4 w-4" />
                  {/* Badge for notifications - hidden for now */}
                  {/* <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">3</span> */}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Notifications</TooltipContent>
            </Tooltip>

            {/* Settings (Admin only) */}
            {isAdmin && (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className={`h-8 w-8 ${topBarText}`}
                    onClick={() => navigate('/settings')}
                    data-testid="settings-nav-btn"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Settings</TooltipContent>
              </Tooltip>
            )}

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className={`h-8 gap-2 px-2 ${topBarText} ${isLightMode ? 'hover:bg-gray-100' : 'hover:bg-zinc-800'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isLightMode ? 'bg-gray-200' : 'bg-zinc-700'}`}>
                    <User className={`h-3.5 w-3.5 ${isLightMode ? 'text-gray-600' : 'text-zinc-300'}`} />
                  </div>
                  <span className={`hidden md:block text-xs font-medium max-w-[100px] truncate ${isLightMode ? 'text-gray-700' : 'text-zinc-300'}`}>
                    {user?.full_name}
                  </span>
                  <ChevronDown className={`h-3 w-3 hidden md:block ${isLightMode ? 'text-gray-500' : 'text-zinc-400'}`} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.username} • {user?.role}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setPreferencesOpen(true)}>
                  <Globe className="h-4 w-4 mr-2" />
                  Preferences
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleTheme}>
                  {theme === 'dark' ? <Sun className="h-4 w-4 mr-2" /> : <Moon className="h-4 w-4 mr-2" />}
                  {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Layout (below top bar) */}
        <div className="flex pt-12 min-h-screen">
          {/* Sidebar */}
          <aside
            className={`fixed top-12 left-0 h-[calc(100vh-3rem)] ${sidebarBg} transition-all duration-200 z-40 flex flex-col ${
              sidebarCollapsed ? 'w-14' : 'w-56'
            } ${
              mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            } md:translate-x-0`}
          >
            {/* Navigation */}
            <ScrollArea className="flex-1 py-3">
              <nav className={`space-y-1 ${sidebarCollapsed ? 'px-2' : 'px-2'}`}>
                {navItems.map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </nav>
            </ScrollArea>

            {/* Version Number */}
            <div className={`px-3 py-2 ${sidebarCollapsed ? 'text-center' : 'flex items-center justify-between'}`}>
              <span className="text-[10px] text-zinc-500 font-mono" data-testid="app-version">
                {getVersionDisplay(sidebarCollapsed)}
              </span>
              {!sidebarCollapsed && getEnvironment() !== 'PROD' && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                  getEnvironment() === 'STAGING' ? 'bg-amber-500/20 text-amber-400' :
                  getEnvironment() === 'PREVIEW' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-green-500/20 text-green-400'
                }`}>
                  {getEnvironment()}
                </span>
              )}
            </div>

            {/* Collapse Toggle at Bottom */}
            <div className={`border-t ${sidebarBorder} p-2 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    data-testid="sidebar-collapse-btn"
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${sidebarText} ${sidebarHover} transition-colors ${
                      sidebarCollapsed ? 'justify-center w-10 h-10' : 'w-full'
                    }`}
                  >
                    {sidebarCollapsed ? (
                      <PanelLeftOpen className="h-4 w-4" />
                    ) : (
                      <>
                        <PanelLeftClose className="h-4 w-4" />
                        <span className="hidden md:inline">Collapse</span>
                      </>
                    )}
                  </button>
                </TooltipTrigger>
                {sidebarCollapsed && (
                  <TooltipContent side="right">Expand sidebar</TooltipContent>
                )}
              </Tooltip>
            </div>
          </aside>

          {/* Overlay for mobile */}
          {mobileMenuOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-30 md:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}

          {/* Main Content */}
          <main className={`flex-1 transition-all duration-200 ${
            sidebarCollapsed ? 'md:ml-14' : 'md:ml-56'
          }`}>
            {/* Impersonation Banner */}
            {isImpersonating && (
              <div className="bg-orange-500 text-white px-4 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCog className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Impersonating: <strong>{user?.full_name}</strong> ({user?.client_id})
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-white hover:text-white hover:bg-orange-600"
                >
                  <LogOut className="h-4 w-4 mr-1" /> End
                </Button>
              </div>
            )}
            <div className="p-4 md:p-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* User Preferences Dialog */}
      <Dialog open={preferencesOpen} onOpenChange={setPreferencesOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Preferences
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Language Selection */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label className="text-base font-medium">{t('common.language')}</Label>
                <p className="text-sm text-muted-foreground">
                  {t('settings.languageDescription')}
                </p>
              </div>
              <Select value={currentLanguage} onValueChange={changeLanguage}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      <span className="flex items-center gap-2">
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Theme Toggle */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="space-y-1">
                <Label className="text-base font-medium">Appearance</Label>
                <p className="text-sm text-muted-foreground">
                  Switch between light and dark mode
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Sun className={`h-4 w-4 ${theme === 'light' ? 'text-[#ffd108]' : 'text-muted-foreground'}`} />
                <Switch
                  checked={theme === 'dark'}
                  onCheckedChange={toggleTheme}
                />
                <Moon className={`h-4 w-4 ${theme === 'dark' ? 'text-[#ffd108]' : 'text-muted-foreground'}`} />
              </div>
            </div>

            {/* User Info */}
            <div className="space-y-3 pt-4 border-t">
              <Label className="text-base font-medium">Account</Label>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name</span>
                  <span>{user?.full_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Username</span>
                  <span className="font-mono">{user?.username}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Role</span>
                  <span className="capitalize">{user?.role}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Company</span>
                  <span>{user?.company_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Client ID</span>
                  <span className="font-mono text-xs">{user?.client_id}</span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreferencesOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </TooltipProvider>
  );
};

export default Layout;
